import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Activity, Building2, Download, ListChecks, Wallet } from 'lucide-react';
import { initialsOf, hashHue } from '@/utils/colors';
import { downloadTableExcel } from '@/utils/exportTable';
import { useTzShiftMs } from '@/hooks/useTzShift';
import type { ID } from '@/types';
import {
  computeWorkloadRows, currentMonthKey, monthLabel,
  WORKLOAD_GAMES_HEAD, workloadGameRow,
} from '@/utils/workload';
import { WorkloadGamesList } from '../workload/WorkloadGames';
import { getDealPayroll, getMyAgentSaleDealIds } from '@/auth/adminClient';
import type { WorkloadGame } from '@/utils/workload';

/**
 * «Мои игры» — личный список отработанных игр за месяц у самого сотрудника.
 *
 * Зачем: в конце месяца ведущий приходит к кассиру со своим списком игр, кассир — со своим
 * (вкладка «Персонал → Загрузка»). Считает их ОДИН и тот же код (src/utils/workload.ts),
 * поэтому сверять больше нечего — цифры совпадают по построению.
 *
 * Доступ: любому, у кого заполнен employee_id (привязка к карточке сотрудника). Отдельного
 * права нет — человек видит только СВОИ игры, чужих строк на экране не бывает.
 * Зарплата за игру — считает сервер (см. getDealPayroll/payroll/calc.ts), сам справочник
 * ставок сюда не попадает, только готовая сумма (или причина, если не хватает данных).
 */
export function MyGamesPage() {
  const { employees, teams, deals, branches, game_formats, filterBranchId, setModal, currentUser } = useStore();
  const toast = useStore((s) => s.toast);
  const tzShiftMs = useTzShiftMs();
  const [month, setMonth] = useState(currentMonthKey);

  const employeeId = currentUser?.employee_id ?? null;
  const branchById = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);

  // Считаем ровно по себе: тот же расчёт, что и в своде «Загрузка», но с отсечкой по своему
  // employee_id. Фильтр филиала оставляем 'all' — свои игры человек должен видеть все,
  // независимо от того, какой филиал сейчас выбран в шапке.
  const rows = useMemo(
    () => computeWorkloadRows({ deals, employees, teams, month, branchId: 'all', onlyEmployeeId: employeeId }),
    [deals, employees, teams, month, employeeId],
  );
  const mine = rows[0] ?? null;
  const items = mine?.items ?? [];
  const hours = mine?.hours ?? 0;

  const myName = employees.find((e) => e.id === employeeId)?.full_name ?? currentUser?.full_name ?? 'Мои игры';

  // Агентские 5% за личную продажу (чат 02.11.2026) — НЕ зависят от состава смены, поэтому
  // такая игра могла не попасть в items (отработанные). Доливаем отдельным запросом, иначе
  // сотрудник вообще не узнает, что по ней есть агентские.
  const [agentDealIds, setAgentDealIds] = useState<string[]>([]);
  useEffect(() => {
    if (!employeeId) { setAgentDealIds([]); return; }
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return;
    const fromMs = new Date(y, m - 1, 1).getTime();
    const toMs = new Date(y, m, 1).getTime();
    let cancelled = false;
    getMyAgentSaleDealIds(fromMs, toMs).then((ids) => { if (!cancelled) setAgentDealIds(ids); }).catch(() => { if (!cancelled) setAgentDealIds([]); });
    return () => { cancelled = true; };
  }, [employeeId, month]);

  // Список для отображения/выгрузки/расчёта денег = отработанные игры + игры, где я только
  // продал как агент (роль «agent»), без дублей. «Игр за месяц»/«часов» в шапке — по-прежнему
  // только отработанные (items), агентская продажа — не работа.
  const displayItems = useMemo<WorkloadGame[]>(() => {
    const extra: WorkloadGame[] = [];
    for (const id of agentDealIds) {
      const deal = deals.find((d) => d.id === id);
      if (deal) extra.push({ deal, role: 'agent' });
    }
    return [...items, ...extra];
  }, [items, agentDealIds, deals]);

  // Зарплата за игру — считает сервер (справочник ставок наружу не отдаёт), тянем по одной
  // игре, по месяцу их немного. null = сервер явно сказал «не хватает данных для расчёта».
  const [payByDeal, setPayByDeal] = useState<Map<string, number | null>>(new Map());
  const [payReasonByDeal, setPayReasonByDeal] = useState<Map<string, string | undefined>>(new Map());
  useEffect(() => {
    let cancelled = false;
    setPayByDeal(new Map());
    setPayReasonByDeal(new Map());
    (async () => {
      // Дубли деталей (одна игра могла попасть и в отработанные, и в агентские) запрашиваем
      // по сделке один раз.
      const dealIds = [...new Set(displayItems.map((it) => it.deal.id))];
      for (const dealId of dealIds) {
        if (cancelled) return;
        try {
          const rows = await getDealPayroll(dealId);
          // На одну игру у ОДНОГО сотрудника может быть НЕСКОЛЬКО строк — за роль на игре
          // (ведущий/диджей) и отдельно агентские, если он же её продал. Складываем все
          // удавшиеся; если ни одна не посчиталась — показываем причину первой.
          const mineRows = rows.filter((r) => r.employee_id === employeeId);
          const okRows = mineRows.filter((r) => r.ok);
          if (!cancelled) {
            if (okRows.length > 0) {
              setPayByDeal((m) => new Map(m).set(dealId, okRows.reduce((sum, r) => sum + (r.total ?? 0), 0)));
            } else {
              setPayByDeal((m) => new Map(m).set(dealId, null));
              if (mineRows[0]) setPayReasonByDeal((m) => new Map(m).set(dealId, mineRows[0].reason));
            }
          }
        } catch {
          if (!cancelled) setPayByDeal((m) => new Map(m).set(dealId, null));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [displayItems, employeeId]);
  const payTotal = useMemo(() => [...payByDeal.values()].reduce((sum: number, v) => sum + (v ?? 0), 0), [payByDeal]);
  const payUnresolvedCount = useMemo(() => displayItems.filter((it) => payByDeal.get(it.deal.id) === null).length, [displayItems, payByDeal]);

  const openDeal = (id: ID) => setModal({ kind: 'deal', id }, displayItems.map((it) => it.deal.id));

  const exportGames = () => {
    const rowsOut = displayItems.map((it) => workloadGameRow({
      employeeName: myName,
      item: it,
      formats: game_formats,
      branchName: branchById.get(it.deal.branch_id ?? '')?.name ?? '',
      tzShiftMs,
    }));
    downloadTableExcel(`Мои-игры-${month}.xlsx`, WORKLOAD_GAMES_HEAD, rowsOut);
    toast({ kind: 'success', text: 'Список игр выгружен' });
  };

  // Экран открывается только у привязанных к карточке сотрудника (см. TopNav/App), но если
  // привязку сняли прямо во время работы — говорим об этом по-человечески, а не пустой таблицей.
  if (!employeeId) {
    return (
      <div className="h-full grid place-items-center bg-surface-0 text-ink-2 p-6 text-center">
        <div className="space-y-2 max-w-sm">
          <div className="text-lg font-semibold text-ink-1">Личный список игр недоступен</div>
          <p className="text-sm">Ваш вход не привязан к карточке сотрудника — попросите руководителя связать их, и здесь появятся ваши игры.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="min-h-14 px-3 sm:px-5 py-2 sm:py-0 flex items-center flex-wrap gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-8 h-8 rounded-full grid place-items-center text-white font-bold text-[11px] flex-shrink-0"
            style={{ backgroundColor: `hsl(${hashHue(myName)}, 55%, 48%)` }}>
            {initialsOf(myName)}
          </span>
          <span className="font-semibold text-ink-1 truncate">{myName}</span>
        </div>

        <div className="flex-1" />

        <input
          type="month"
          className="input h-9 w-[160px]"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          title="Месяц"
        />
        <button className="btn-soft" onClick={exportGames} disabled={displayItems.length === 0}
          title="Выгрузить свой список игр за месяц">
          <Download className="w-4 h-4" /> Excel
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-5">
        <div className="max-w-[1000px] mx-auto space-y-3">
          {/* Итог за месяц — сколько игр и часов. Сумм и денег здесь нет. */}
          <div className="panel p-4 flex items-center flex-wrap gap-4">
            <div className="flex items-center gap-2 text-ink-2 text-sm">
              <ListChecks className="w-4 h-4 text-accent" />
              {monthLabel(month)}
            </div>
            <div className="flex-1" />
            <div className="text-center">
              <div className="text-2xl font-bold text-ink-1 tabular-nums">{items.length}</div>
              <div className="text-[11px] text-ink-3">игр за месяц</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-ink-1 tabular-nums">{hours.toFixed(1)}</div>
              <div className="text-[11px] text-ink-3">часов</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent tabular-nums">{payTotal.toLocaleString('ru-RU')} ₸</div>
              <div className="text-[11px] text-ink-3 flex items-center gap-1 justify-center">
                <Wallet className="w-3 h-3" /> зарплата за месяц
              </div>
            </div>
          </div>
          {payUnresolvedCount > 0 && (
            <p className="text-[11px] text-ink-3 px-1">
              По {payUnresolvedCount} {payUnresolvedCount === 1 ? 'игре' : 'играм'} сумма не посчиталась — не хватает данных (уровень/ставка/формат). Откройте игру — там видна причина.
            </p>
          )}

          {displayItems.length === 0 ? (
            <div className="panel p-8 text-center">
              <Activity className="w-10 h-10 text-ink-3 mx-auto mb-2" />
              <p className="text-sm text-ink-2">За этот месяц проведённых игр у вас нет</p>
            </div>
          ) : (
            <div className="panel p-0 overflow-hidden">
              <div className="overflow-x-auto p-2 sm:p-3">
                <WorkloadGamesList items={displayItems} formats={game_formats} tzShiftMs={tzShiftMs} onOpenDeal={openDeal} payByDeal={payByDeal} payReasonByDeal={payReasonByDeal} />
              </div>
            </div>
          )}

          {/* Подсказка про филиал: список личный и филиалом в шапке не режется. */}
          {filterBranchId !== 'all' && (
            <p className="text-[11px] text-ink-3 flex items-center gap-1 px-1">
              <Building2 className="w-3 h-3" /> Показаны все ваши игры — выбранный в шапке филиал этот список не ограничивает.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
